import { computed, shallowRef, type Ref } from 'vue'
import type {
  GraphicsRequirementDeclaration,
  GraphicsRequirementIssue,
  GraphicsRequirementSubjectIdentity,
} from '@sharefe/share3d-engine'
import {
  readGraphicsUrlConfig,
  writeGraphicsUrlBackend,
  type ExampleGraphicsBackend,
} from '../utils/graphicsUrlConfig'

/** 静态预检返回的阻塞项（与引擎 GraphicsRequirementIssue 结构一致）。 */
export type GraphicsBlockerLike = GraphicsRequirementIssue

export type GraphicsRequirementSubjectLike = {
  readonly subject: GraphicsRequirementDeclaration
  readonly identity: GraphicsRequirementSubjectIdentity
}

export type GraphicsInitPhase = 'idle' | 'creating' | 'ready' | 'error'

export interface GraphicsRuntimeError {
  readonly message: string
  /** 结构化阻塞项（若运行时错误携带）。 */
  readonly blockers?: readonly GraphicsBlockerLike[]
}

export interface UseGraphicsBackendStateOptions {
  /** 惰性提供该 route 的插件主体，用于静态预检；缺省时跳过静态预检。 */
  resolveSubjects?: () => Promise<readonly GraphicsRequirementSubjectLike[]>
  /** 覆盖静态预检实现（默认使用引擎的 collectStaticGraphicsRequirementIssues）。 */
  collectIssues?: (
    subjects: readonly GraphicsRequirementSubjectLike[],
    profile: { readonly kind: ExampleGraphicsBackend }
  ) => readonly GraphicsBlockerLike[]
  /** 覆盖重建入口（默认写回 URL 后 reload）。 */
  rebuild?: (backend: ExampleGraphicsBackend) => void
  /**
   * 锁定页面的固定后端：覆盖 URL 请求值，让卡片展示页面真实请求的条件。
   * 仅影响展示（卡片在这类页面不提供切换），不改变引擎创建参数。
   */
  lockedBackend?: ExampleGraphicsBackend
}

/**
 * 维护 example 页面的 graphics 状态：请求/实际 backend、静态预检、初始化阶段、运行时错误与重建。
 *
 * 静态预检只读插件声明，不创建 renderer/device；`unresolvedRequirements` 表示「需运行时确认」，
 * 不会被当作通过。
 */
export function useGraphicsBackendState(options: UseGraphicsBackendStateOptions = {}) {
  const requestedBackend = shallowRef<ExampleGraphicsBackend>(
    options.lockedBackend ?? readGraphicsUrlConfig().requestedBackend
  )
  const actualBackend = shallowRef<ExampleGraphicsBackend | null>(null)
  const phase = shallowRef<GraphicsInitPhase>('idle')
  const blockers = shallowRef<readonly GraphicsBlockerLike[]>([])
  const runtimeError = shallowRef<GraphicsRuntimeError | null>(null)

  const needsRuntimeConfirmation = computed(() =>
    blockers.value.filter((blocker) => (blocker.unresolvedRequirements?.length ?? 0) > 0)
  )
  const confirmedBlockers = computed(() =>
    blockers.value.filter((blocker) => (blocker.missingCapabilities?.length ?? 0) > 0)
  )

  async function runPreflight(backend: ExampleGraphicsBackend = requestedBackend.value) {
    if (!options.resolveSubjects) {
      blockers.value = []
      return []
    }
    const subjects = await options.resolveSubjects()
    const collect = options.collectIssues ?? (await loadDefaultCollector())
    const issues = collect(subjects, { kind: backend })
    blockers.value = issues
    return issues
  }

  function selectBackend(backend: ExampleGraphicsBackend): void {
    requestedBackend.value = backend
  }

  function markCreating(): void {
    phase.value = 'creating'
    runtimeError.value = null
  }

  function markReady(backend: ExampleGraphicsBackend | null): void {
    actualBackend.value = backend
    phase.value = 'ready'
    runtimeError.value = null
  }

  function markError(error: GraphicsRuntimeError): void {
    phase.value = 'error'
    runtimeError.value = error
  }

  function rebuild(backend: ExampleGraphicsBackend = requestedBackend.value): void {
    if (options.rebuild) {
      options.rebuild(backend)
      return
    }
    window.location.hash = writeGraphicsUrlBackend(window.location.hash, backend)
    window.location.reload()
  }

  return {
    requestedBackend: requestedBackend as Ref<ExampleGraphicsBackend>,
    actualBackend,
    phase,
    blockers,
    confirmedBlockers,
    needsRuntimeConfirmation,
    runtimeError,
    runPreflight,
    selectBackend,
    markCreating,
    markReady,
    markError,
    rebuild,
  }
}

async function loadDefaultCollector() {
  const engine = await import('@sharefe/share3d-engine')
  const collect = engine.collectStaticGraphicsRequirementIssues
  if (!collect) {
    throw new Error('引擎未导出 collectStaticGraphicsRequirementIssues')
  }
  return collect
}
